<template>
  <div class="relative w-full" ref="wrapper">
    <!-- Trigger button mimicking the native select -->
    <button
      type="button"
      :disabled="disabled"
      @click="!disabled && toggle()"
      @focus="$emit('focus')"
      :class="[
        'glass-select-trigger mt-1 block w-full pl-3 pr-10 py-2 text-left text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
        error
          ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
          : 'focus:ring-blue-500 focus:border-blue-500',
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      ]"
    >
      <span :class="hasValue ? '' : ''">
        {{ selectedLabel }}
      </span>
      <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <svg
          class="h-4 w-4 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </span>
    </button>
    <!-- Hidden mirror so a parent <form> picks up HTML5 required-field
         validation when the user submits without selecting a value. The
         input is positioned but invisible so the browser's "Please fill
         out this field" anchor lands roughly on the visible trigger. -->
    <input
      v-if="required"
      class="glass-select-mirror"
      tabindex="-1"
      aria-hidden="true"
      :required="required"
      :value="hasValue ? String(modelValue) : ''"
      @invalid="onMirrorInvalid"
      @input="() => {}"
    />

    <!-- Dropdown panel teleported to body to escape overflow:hidden ancestors -->
    <Teleport to="body">
      <Transition :name="dropUp ? 'glass-dropdown-up' : 'glass-dropdown'">
        <div
          v-if="isOpen"
          class="glass-card fixed z-[9999] rounded-md overflow-hidden"
          :style="dropdownStyle"
        >
          <!-- Search box, only when searchable=true. Lets the dropdown handle
               long option lists (e.g. 283 probes in prod) without forcing
               consumers to roll their own typeahead. -->
          <div
            v-if="searchable"
            class="px-2 pt-2 pb-1"
          >
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              :placeholder="searchPlaceholder"
              class="glass-select-search w-full px-2 py-1 text-sm rounded border focus:outline-none focus:ring-2 focus:ring-blue-500"
              @click.stop
              @keydown.esc.stop="isOpen = false"
            />
          </div>
          <ul class="max-h-60 overflow-y-auto py-1">
            <li
              v-if="!multiple && (!searchable || !searchQuery)"
              @click="select('')"
              :class="['glass-select-option px-3 py-2 cursor-pointer select-none text-sm', !hasValue ? 'glass-select-option--active' : '']"
            >
              {{ placeholder }}
            </li>
            <!-- click.stop like the search input above: the dropdown is teleported to
                 <body>, so without it an option click reaches the document-level
                 outside-click handler, which sees a target outside the wrapper and
                 closes the list. In multiple mode select() deliberately leaves the
                 list open, and that only works if the click stops here. -->
            <li
              v-for="opt in filtered"
              :key="opt.value"
              @click.stop="select(opt.value)"
              :class="['glass-select-option px-3 py-2 cursor-pointer select-none text-sm', multiple ? 'flex items-center gap-2' : '', isSelected(opt.value) ? 'glass-select-option--active' : '']"
            >
              <input
                v-if="multiple"
                type="checkbox"
                :checked="isSelected(opt.value)"
                class="h-3.5 w-3.5 accent-primary shrink-0 pointer-events-none"
                tabindex="-1"
              />
              <span>{{ opt.label }}</span>
            </li>
            <li
              v-if="searchable && filtered.length === 0"
              class="px-3 py-2 text-sm text-gray-500 select-none"
            >
              No matches
            </li>
          </ul>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'GlassSelect',
  props: {
    modelValue: { default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Select an option' },
    valueKey: { type: String, default: 'id' },
    labelKey: { type: String, default: 'name' },
    error: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    // Searchable mode adds an input at the top of the dropdown that filters
    // the option list by label (case-insensitive). Required for long lists
    // like probes (283 in prod) and sites; safe to leave off for short
    // enums.
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { type: String, default: 'Search...' },
    // Multi-select mode: modelValue is an array of values instead of a single
    // value. Clicking an option toggles membership and the dropdown stays
    // open so the user can pick several without re-opening it each time.
    multiple: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'focus', 'change'],
  data() {
    return { isOpen: false, dropUp: false, triggerRect: null, searchQuery: '' };
  },
  computed: {
    normalized() {
      return this.options.map(opt => {
        if (typeof opt === 'string') return { value: opt, label: opt };
        return { value: opt[this.valueKey], label: opt[this.labelKey] };
      });
    },
    filtered() {
      if (!this.searchable || !this.searchQuery) return this.normalized;
      const q = this.searchQuery.toLowerCase();
      return this.normalized.filter(o =>
        String(o.label).toLowerCase().includes(q)
      );
    },
    hasValue() {
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.length > 0;
      }
      return this.modelValue !== '' && this.modelValue !== null && this.modelValue !== undefined;
    },
    selectedLabel() {
      if (!this.hasValue) return this.placeholder;
      if (this.multiple) {
        return this.modelValue
          .map(v => {
            const found = this.normalized.find(o => o.value == v);
            return found ? found.label : v;
          })
          .join(', ');
      }
      const found = this.normalized.find(o => o.value == this.modelValue);
      return found ? found.label : this.placeholder;
    },
    dropdownStyle() {
      if (!this.triggerRect) return {};
      if (this.dropUp) {
        return {
          bottom: `${window.innerHeight - this.triggerRect.top + 4}px`,
          left: `${this.triggerRect.left}px`,
          width: `${this.triggerRect.width}px`,
        };
      }
      return {
        top: `${this.triggerRect.bottom + 4}px`,
        left: `${this.triggerRect.left}px`,
        width: `${this.triggerRect.width}px`,
      };
    },
  },
  methods: {
    toggle() {
      if (this.isOpen) {
        this.isOpen = false;
        return;
      }
      const rect = this.$refs.wrapper.getBoundingClientRect();
      this.triggerRect = rect;
      const spaceBelow = window.innerHeight - rect.bottom;
      this.dropUp = spaceBelow < 260;
      this.isOpen = true;
      if (this.searchable) {
        // Reset filter every open, then focus the search box so the user
        // can type immediately without an extra click.
        this.searchQuery = '';
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
    },
    isSelected(value) {
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.includes(value);
      }
      return this.modelValue == value;
    },
    select(value) {
      if (this.multiple) {
        const next = Array.isArray(this.modelValue) ? this.modelValue.slice() : [];
        const idx = next.indexOf(value);
        if (idx === -1) next.push(value); else next.splice(idx, 1);
        this.$emit('update:modelValue', next);
        this.$emit('change', next);
        // Leave the dropdown open: picking several apps one click at a time
        // shouldn't require re-opening the list each time.
        return;
      }
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
      this.isOpen = false;
      this.searchQuery = '';
    },
    handleClickOutside(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.isOpen = false;
      }
    },
    handleScroll() {
      if (this.isOpen && this.$refs.wrapper) {
        this.triggerRect = this.$refs.wrapper.getBoundingClientRect();
      }
    },
    onMirrorInvalid(event) {
      // Surface the trigger to the user when the browser's required-field
      // validation fails. The hidden mirror is the form participant; pulling
      // focus to the wrapper's button gives the user something to act on.
      event.preventDefault();
      const trigger = this.$refs.wrapper && this.$refs.wrapper.querySelector('button');
      if (trigger) trigger.focus();
    },
    handleTutorialSelect(e) {
      this.select(e.detail.value);
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    window.addEventListener('scroll', this.handleScroll, true);
    this.$refs.wrapper.addEventListener('tutorial:select', this.handleTutorialSelect);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('scroll', this.handleScroll, true);
    this.$refs.wrapper.removeEventListener('tutorial:select', this.handleTutorialSelect);
  },
};
</script>

<style scoped>
/* Hidden mirror input: positioned to overlap the trigger so the browser's
   "Please fill out this field" tooltip points at the right place, but
   visually transparent and not interactive. */
.glass-select-mirror {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
}

/* Trigger — exact same rules as the global select{} in base.css */
@media (prefers-color-scheme: dark) {
  .glass-select-trigger {
    background-color: oklch(44.6% 0.03 256.802 / 0.5);
    backdrop-filter: blur(12px);
    color: #f2f2f2;
  }
  .glass-select-trigger:not(.border-red-500) {
    border: 1px solid rgb(88, 93, 97);
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
  }
  .glass-select-trigger.border-red-500 {
    border: 1px solid rgb(239 68 68);
  }
}

@media (prefers-color-scheme: light) {
  .glass-select-trigger {
    background-color: oklch(100% 0 0 / 0.5) !important;
    backdrop-filter: blur(12px);
    color: #1a1a1a;
  }
  .glass-select-trigger:not(.border-red-500) {
    border: 1px solid oklch(90% 0.01 256.802);
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.05);
  }
  .glass-select-trigger.border-red-500 {
    border: 1px solid rgb(239 68 68);
  }
}

/* Options */
.glass-select-option {
  @apply text-gray-900;
}

@media (prefers-color-scheme: dark) {
  .glass-select-option {
    @apply text-gray-100;
  }
}

.glass-select-option:hover {
  @apply bg-black/5;
}

@media (prefers-color-scheme: dark) {
  .glass-select-option:hover {
    @apply bg-white/10;
  }
}

.glass-select-option--active {
  @apply bg-black/10 font-medium;
}

@media (prefers-color-scheme: dark) {
  .glass-select-option--active {
    @apply bg-white/15 font-medium;
  }
}

/* Drop-down transition */
.glass-dropdown-enter-active,
.glass-dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.glass-dropdown-enter-from,
.glass-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Drop-up transition */
.glass-dropdown-up-enter-active,
.glass-dropdown-up-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.glass-dropdown-up-enter-from,
.glass-dropdown-up-leave-to {
  opacity: 0;
  transform: translateY(6px);
}
</style>
